import type { Account, Expense, ExpenseCategory } from '@erp/shared';
import { ArrowRight, Ban, Banknote, Calendar, Coins, Download, FileText, FolderTree, type LucideIcon, Paperclip, Pencil, Plus, Receipt, Trash2, TrendingDown, Upload, Wallet } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Badge,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
} from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Button,
  Combobox,
  type ComboboxOption,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  NumberInput,
  StatCard,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { formatAccountBalance, loadAccountBalances, type AccountBalanceMap } from '../../lib/accountBalances.js';
import { api } from '../../lib/api.js';
import {
  type Attachment,
  deleteAttachment,
  fetchAttachmentObjectUrl,
  isImageMime,
  listAttachments,
  uploadAttachment,
} from '../../lib/attachments.js';
import { useAuthStore } from '../../lib/authStore.js';
import { appUrl } from '../../lib/basePath.js';
import {
  createExpense,
  createExpenseCategory,
  deleteExpense,
  listExpenseCategories,
  listExpenses,
  updateExpense,
  voidExpense,
} from '../../lib/expenses.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { formatBytes } from '../common/FileUploadDialog.js';
import { FxEquivalent } from '../common/FxEquivalent.js';

const NONE = '__none__';

function acctOptions(accounts: Account[], type: Account['accountType'], sub?: (id: string) => string | undefined): ComboboxOption[] {
  const scoped = accounts.filter((a) => a.isActive && a.accountType === type);
  const list = scoped.length ? scoped : accounts.filter((a) => a.isActive);
  return list.map((a) => ({ value: a.id, label: `${a.code} — ${a.name}`, keywords: `${a.code} ${a.name}`, sublabel: sub?.(a.id) }));
}

export function ExpensesPage(): JSX.Element {
  const { t, i18n } = useTranslation('expenses');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('expenses.write');
  const canAttachRead = usePermission('attachments.read');
  const canAttachWrite = usePermission('attachments.write');
  const canAttachDelete = usePermission('attachments.delete');
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';

  const [expenses, setExpenses] = useState<Expense[] | null>(null);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [catOpen, setCatOpen] = useState(false);
  const [receiptsFor, setReceiptsFor] = useState<Expense | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [warehouseOptions, setWarehouseOptions] = useState<Array<{ id: string; name: string }>>([]);

  const reload = async (): Promise<void> => {
    setExpenses(await listExpenses({ from: from || undefined, to: to || undefined, status: status || undefined, warehouseId: warehouseFilter || undefined }));
  };
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, status, warehouseFilter]);
  useEffect(() => {
    let on = true;
    void api().get<{ items: Array<{ id: string; name: string; isActive: boolean }> }>('/warehouses')
      .then((r) => { if (on) setWarehouseOptions(r.data.items.filter((w) => w.isActive)); })
      .catch(() => undefined);
    return () => { on = false; };
  }, []);
  const reloadCategories = (): void => { void listExpenseCategories().then(setCategories).catch(() => undefined); };
  useEffect(() => {
    reloadCategories();
    void api().get<{ items: Account[] }>('/accounts').then((r) => setAccounts(r.data.items)).catch(() => undefined);
  }, []);

  const locale = i18n.language;
  const m = (amount: string, currency: string): string => formatMoney(amount, { locale, currency });

  // KPIs reflect the currently-shown (filtered) list, summed per currency.
  const totals = useMemo(() => {
    const byCcy = new Map<string, number>();
    for (const e of expenses ?? []) {
      if (e.status !== 'posted') continue;
      byCcy.set(e.currencyCode, (byCcy.get(e.currencyCode) ?? 0) + Number(e.amount));
    }
    return [...byCcy.entries()];
  }, [expenses]);

  const onVoid = async (e: Expense): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmVoid', { number: e.expenseNumber }), tone: 'danger' }))) return;
    try {
      await voidExpense(e.id);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onDelete = async (e: Expense): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmDelete', { number: e.expenseNumber, defaultValue: `Delete expense ${e.expenseNumber}? This reverses its journal entry and removes it from the list.` }), tone: 'danger' }))) return;
    try {
      await deleteExpense(e.id);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const columns: DataTableColumn<Expense>[] = [
    {
      id: 'number', header: t('columns.number'), sortValue: (e) => e.expenseNumber, searchValue: (e) => e.expenseNumber,
      // The number links to the expense's journal voucher (opens in a new tab,
      // window.open keeps the auth session).
      cell: (e) => (e.journalEntryId ? (
        <button
          type="button"
          className="font-mono text-xs text-brand hover:underline"
          onClick={() => window.open(appUrl(`/accounting/journals/${e.journalEntryId}/print`), '_blank')}
          title={t('viewVoucher')}
        >
          {e.expenseNumber}
        </button>
      ) : <span className="font-mono text-xs">{e.expenseNumber}</span>),
    },
    {
      id: 'date', header: t('columns.date'), sortValue: (e) => e.expenseDate,
      cell: (e) => formatDate(e.expenseDate, { locale }),
    },
    {
      id: 'category', header: t('columns.category'), sortValue: (e) => e.categoryName ?? '',
      searchValue: (e) => e.categoryName ?? '', cell: (e) => e.categoryName ?? <span className="text-fg-muted">—</span>,
    },
    {
      id: 'branch', header: t('columns.branch', { defaultValue: 'Branch' }),
      sortValue: (e) => e.warehouseName ?? '',
      cell: (e) => e.warehouseName ?? <span className="text-fg-muted">—</span>,
    },
    {
      id: 'payee', header: t('columns.payee'), sortValue: (e) => e.payee ?? '', searchValue: (e) => e.payee ?? '',
      cellClassName: 'font-medium', cell: (e) => e.payee ?? <span className="text-fg-muted">—</span>,
    },
    {
      id: 'expenseAccount', header: t('columns.expenseAccount'), sortValue: (e) => e.expenseAccountName ?? '',
      cellClassName: 'text-fg-muted', cell: (e) => e.expenseAccountName ?? '—',
    },
    {
      id: 'paidFrom', header: t('columns.paidFrom'), sortValue: (e) => e.paidFromAccountName ?? '',
      cellClassName: 'text-fg-muted', cell: (e) => e.paidFromAccountName ?? '—',
    },
    {
      id: 'amount', header: t('columns.amount'), align: 'end', sortValue: (e) => Number(e.amount),
      cellClassName: 'font-mono tabular-nums',
      cell: (e) => <span className={e.status === 'void' ? 'text-fg-muted line-through' : ''}>{m(e.amount, e.currencyCode)}</span>,
    },
    {
      id: 'status', header: t('columns.status'), sortValue: (e) => e.status,
      cell: (e) => <Badge variant={e.status === 'posted' ? 'success' : 'secondary'}>{t(`status.${e.status}`)}</Badge>,
    },
    {
      id: 'actions', header: '', align: 'end' as const, headClassName: 'w-28',
      cell: (e: Expense) => (
        <div className="flex justify-end gap-1">
          {canAttachRead && (
            <Button variant="ghost" size="sm" onClick={() => setReceiptsFor(e)} title={t('receipts.view')} aria-label={`${t('receipts.view')} ${e.expenseNumber}`}>
              <Paperclip className="h-4 w-4" aria-hidden />
            </Button>
          )}
          {canWrite && e.status === 'posted' && (
            <Button variant="ghost" size="sm" onClick={() => { setEditing(e); setShowForm(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} title={t('actions.edit')} aria-label={`${t('actions.edit')} ${e.expenseNumber}`}>
              <Pencil className="h-4 w-4" aria-hidden />
            </Button>
          )}
          {canWrite && e.status === 'posted' && (
            <Button variant="ghost" size="sm" onClick={() => void onVoid(e)} title={t('actions.void')} aria-label={`${t('actions.void')} ${e.expenseNumber}`}>
              <Ban className="h-4 w-4" aria-hidden />
            </Button>
          )}
          {canWrite && (
            <Button variant="ghost" size="sm" onClick={() => void onDelete(e)} title={tc('actions.delete', { defaultValue: 'Delete' })} aria-label={`${tc('actions.delete', { defaultValue: 'Delete' })} ${e.expenseNumber}`}>
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t('description')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setCatOpen(true)}>
            <FolderTree className="me-1.5 h-4 w-4" aria-hidden />{t('manageCategories')}
          </Button>
          {canWrite && !showForm && (
            <Button onClick={() => setShowForm(true)}><Plus className="me-1.5 h-4 w-4" aria-hidden />{t('new')}</Button>
          )}
        </div>
      </header>

      {expenses && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {(totals.length ? totals.slice(0, 2) : [[baseCurrency, 0] as [string, number]]).map(([ccy, amt]) => (
            <StatCard key={ccy} label={t('kpi.total')} value={m(Number(amt).toFixed(4), ccy)} icon={TrendingDown} tone="warning" />
          ))}
          <StatCard label={t('kpi.count')} value={formatNumber(expenses.length, { locale })} icon={Banknote} />
        </div>
      )}

      {(showForm || editing) && (
        <ExpenseForm
          key={editing?.id ?? 'new'}
          expense={editing ?? undefined}
          accounts={accounts}
          categories={categories}
          baseCurrency={baseCurrency}
          canAttach={canAttachWrite}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSaved={async () => { setShowForm(false); setEditing(null); await reload(); }}
        />
      )}

      <Card className="flex flex-wrap items-center gap-3 p-3">
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          {t('filters.from')}
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-auto" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          {t('filters.to')}
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-auto" />
        </label>
        <select
          className="h-9 rounded-md border border-border bg-bg px-3 text-sm text-fg"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={t('filters.status')}
        >
          <option value="">{t('filters.allStatuses')}</option>
          <option value="posted">{t('status.posted')}</option>
          <option value="void">{t('status.void')}</option>
        </select>
        {warehouseOptions.length > 1 && (
          <select
            className="h-9 rounded-md border border-border bg-bg px-3 text-sm text-fg"
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
            aria-label={tc('filters.branch', { defaultValue: 'Branch' })}
          >
            <option value="">{tc('filters.allBranches', { defaultValue: 'All branches' })}</option>
            {warehouseOptions.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        )}
      </Card>

      {!expenses ? (
        <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
      ) : expenses.length === 0 ? (
        <EmptyState
          icon={<Banknote className="h-8 w-8" aria-hidden />}
          title={t('empty')}
          description={t('emptyDescription')}
          action={canWrite ? <Button size="sm" onClick={() => setShowForm(true)}>{t('new')}</Button> : undefined}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={expenses} rowKey={(e) => e.id} searchPlaceholder={t('columns.payee')} />
        </Card>
      )}

      <CategoriesDialog
        open={catOpen}
        onOpenChange={setCatOpen}
        categories={categories}
        accounts={accounts}
        canWrite={canWrite}
        onChanged={reloadCategories}
      />

      <ReceiptsDialog
        expense={receiptsFor}
        canWrite={canAttachWrite}
        canDelete={canAttachDelete}
        onClose={() => setReceiptsFor(null)}
      />
    </div>
  );
}

interface ExpenseFormProps {
  expense?: Expense;
  accounts: Account[];
  categories: ExpenseCategory[];
  baseCurrency: string;
  canAttach: boolean;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function ExpenseForm({ expense, accounts, categories, baseCurrency, canAttach, onCancel, onSaved }: ExpenseFormProps): JSX.Element {
  const { t, i18n } = useTranslation('expenses');
  const { t: tc } = useTranslation();
  const isEdit = !!expense;
  const [date, setDate] = useState(() => expense?.expenseDate ?? new Date().toISOString().slice(0, 10));
  const [categoryId, setCategoryId] = useState<string | undefined>(expense?.categoryId ?? undefined);
  const [expenseAccountId, setExpenseAccountId] = useState<string | undefined>(expense?.expenseAccountId ?? undefined);
  const [paidFromId, setPaidFromId] = useState<string | undefined>(expense?.paidFromAccountId ?? undefined);
  const [payee, setPayee] = useState(expense?.payee ?? '');
  // Trim the stored 4-dp amount ("12000.0000") to a clean editable value.
  const [amount, setAmount] = useState(expense ? String(Number(expense.amount)) : '');
  const [currency, setCurrency] = useState(expense?.currencyCode ?? baseCurrency);
  const [notes, setNotes] = useState(expense?.description ?? '');
  // Branch (warehouse) the money left from. The /warehouses list is already
  // scope-narrowed server-side, so a branch admin only sees their own; with a
  // single option it is preselected and the field renders read-only-ish.
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [warehouseId, setWarehouseId] = useState<string | undefined>(expense?.warehouseId ?? undefined);
  useEffect(() => {
    let on = true;
    void api().get<{ items: Array<{ id: string; name: string; isActive: boolean }> }>('/warehouses')
      .then((r) => {
        if (!on) return;
        const list = r.data.items.filter((w) => w.isActive);
        setWarehouses(list);
        // Default new expenses to the only branch the caller may use.
        if (!expense && list.length === 1) setWarehouseId(list[0]!.id);
      })
      .catch(() => undefined);
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categoryCombo: ComboboxOption[] = [
    { value: NONE, label: t('cat.none') },
    ...categories.filter((c) => c.isActive).map((c) => ({ value: c.id, label: c.name, keywords: c.name })),
  ];
  const expenseAccts = acctOptions(accounts, 'expense');
  // What each cash/bank account holds, in every currency — the paying side.
  // Needs the ledger permission; without it the pickers just show names.
  const [balances, setBalances] = useState<AccountBalanceMap>(new Map());
  useEffect(() => { void loadAccountBalances().then(setBalances).catch(() => undefined); }, []);
  const assetAccts = acctOptions(accounts, 'asset', (id) =>
    balances.size ? `${t('form.balance', { defaultValue: 'Balance' })}: ${formatAccountBalance(balances.get(id), i18n.language, baseCurrency)}` : undefined);

  // Picking a category with a default account pre-fills the expense account.
  const onPickCategory = (v: string | undefined): void => {
    const id = v && v !== NONE ? v : undefined;
    setCategoryId(id);
    const cat = categories.find((c) => c.id === id);
    if (cat?.defaultAccountId && !expenseAccountId) setExpenseAccountId(cat.defaultAccountId);
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!date || !expenseAccountId || !paidFromId || !amount.trim()) {
      setError(t('errors.required'));
      return;
    }
    if (expenseAccountId === paidFromId) { setError(t('errors.sameAccount')); return; }
    if (!(Number(amount) > 0)) { setError(t('errors.invalidAmount')); return; }
    setSaving(true);
    try {
      const payload = {
        expenseDate: date,
        categoryId: categoryId ?? null,
        expenseAccountId,
        paidFromAccountId: paidFromId,
        payee: payee.trim() || null,
        amount: amount.trim(),
        currencyCode: currency,
        description: notes.trim() || null,
        warehouseId: warehouseId ?? null,
      };
      const saved = isEdit ? await updateExpense(expense.id, payload) : await createExpense(payload);
      // Attach the receipt (non-fatal — the expense is already posted).
      if (receiptFile && canAttach) {
        try { await uploadAttachment('expense', saved.id, receiptFile); } catch { /* manage later via the receipts dialog */ }
      }
      await onSaved();
    } catch (err) {
      const data = (err as { response?: { data?: { code?: string; cause?: { code?: string } } } }).response?.data;
      // A branch manager with several branches has to say which one paid.
      if (data?.cause?.code === 'warehouse_required') setError(t('errors.branchRequired'));
      else if (data?.code === 'currency.rate_not_found') setError(tc('fx.rateMissing'));
      else setError(data?.code === 'unprocessable' ? t('errors.sameAccount') : t('errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const fieldLabel = (Icon: LucideIcon, text: string): JSX.Element => (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-fg-subtle" aria-hidden />
      {text}
    </span>
  );
  const acctName = (id?: string): string => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.code} — ${a.name}` : '';
  };
  const previewReady = !!expenseAccountId && !!paidFromId && Number(amount) > 0;

  return (
    <Card className="p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">{isEdit ? t('form.editTitle', { number: expense.expenseNumber }) : t('form.title')}</h2>
        {isEdit && <p className="mt-1 text-xs text-fg-muted">{t('form.editHint')}</p>}
      </div>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={submit} noValidate>
        <FormField id="ex-date" label={fieldLabel(Calendar, t('form.date'))} required>
          <Input id="ex-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </FormField>
        <FormField id="ex-category" label={fieldLabel(FolderTree, t('form.category'))}>
          <Combobox
            id="ex-category"
            options={categoryCombo}
            value={categoryId}
            onChange={onPickCategory}
            placeholder={t('form.searchCategory')}
            ariaLabel={t('form.category')}
          />
        </FormField>
        <FormField id="ex-acct" label={fieldLabel(Banknote, t('form.expenseAccount'))} required>
          <Combobox
            id="ex-acct"
            options={expenseAccts}
            value={expenseAccountId}
            onChange={(v) => setExpenseAccountId(v ?? undefined)}
            placeholder={t('form.searchExpenseAccount')}
            ariaLabel={t('form.expenseAccount')}
          />
        </FormField>
        <FormField id="ex-paid" label={fieldLabel(Wallet, t('form.paidFrom'))} required>
          <Combobox
            id="ex-paid"
            options={assetAccts}
            value={paidFromId}
            onChange={(v) => setPaidFromId(v ?? undefined)}
            placeholder={t('form.searchPaidFrom')}
            ariaLabel={t('form.paidFrom')}
          />
        </FormField>
        <FormField id="ex-amount" label={fieldLabel(Coins, t('form.amount'))} required>
          <NumberInput id="ex-amount" value={amount} onValueChange={setAmount} currency={currency} placeholder="0.00" required />
        </FormField>
        <FormField id="ex-currency" label={t('form.currency')} required>
          <CurrencySelect id="ex-currency" value={currency} onChange={setCurrency} required />
        </FormField>
        {/* Not the base currency: what it will post as, at which rate — or that there is no rate yet. */}
        <FxEquivalent amount={amount} currency={currency} date={date} className="md:col-span-2 -mt-2" />
        {warehouses.length > 1 && (
          <FormField id="ex-branch" label={fieldLabel(Wallet, t('form.branch', { defaultValue: 'Branch' }))}>
            <Combobox
              id="ex-branch"
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              value={warehouseId}
              onChange={(v) => setWarehouseId(v ?? undefined)}
              placeholder={t('form.branchAll', { defaultValue: 'No branch' })}
              ariaLabel={t('form.branch', { defaultValue: 'Branch' })}
            />
          </FormField>
        )}
        <FormField id="ex-payee" label={fieldLabel(Receipt, t('form.payee'))}>
          <Input id="ex-payee" value={payee} onChange={(e) => setPayee(e.target.value)} placeholder={t('form.payeePlaceholder')} maxLength={200} />
        </FormField>
        <FormField id="ex-notes" label={fieldLabel(FileText, t('form.notes'))}>
          <Input id="ex-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('form.notesPlaceholder')} maxLength={1000} />
        </FormField>
        {canAttach && (
          <FormField id="ex-receipt" label={fieldLabel(Paperclip, t('form.receipt'))} helperText={t('form.receiptHint')}>
            <input
              id="ex-receipt"
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-fg-muted file:me-3 file:rounded-md file:border-0 file:bg-bg-subtle file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-fg hover:file:bg-bg-muted"
            />
          </FormField>
        )}
        {/* Live double-entry preview — shows exactly what posts to the GL. */}
        {previewReady && (
          <div className="md:col-span-2 rounded-lg border border-border bg-bg-subtle p-3">
            <div className="mb-2 text-[0.7rem] font-bold uppercase tracking-wide text-fg-muted">{t('form.willPost')}</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
              <span className="inline-flex items-center gap-1.5">
                <span className="rounded bg-success/10 px-1.5 py-0.5 text-[0.7rem] font-bold text-success">{t('form.dr')}</span>
                <span className="font-medium text-fg">{acctName(expenseAccountId)}</span>
              </span>
              <ArrowRight className="h-4 w-4 flex-none text-fg-muted rtl:-scale-x-100" aria-hidden />
              <span className="inline-flex items-center gap-1.5">
                <span className="rounded bg-brand/10 px-1.5 py-0.5 text-[0.7rem] font-bold text-brand">{t('form.cr')}</span>
                <span className="font-medium text-fg">{acctName(paidFromId)}</span>
              </span>
              <span className="ms-auto font-mono text-base font-bold tabular-nums">{formatMoney(amount, { locale: i18n.language, currency })}</span>
            </div>
          </div>
        )}
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{isEdit ? t('form.saveChanges') : t('form.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

interface CategoriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: ExpenseCategory[];
  accounts: Account[];
  canWrite: boolean;
  onChanged: () => void;
}

function CategoriesDialog({ open, onOpenChange, categories, accounts, canWrite, onChanged }: CategoriesDialogProps): JSX.Element {
  const { t } = useTranslation('expenses');
  const { t: tc } = useTranslation();
  const [name, setName] = useState('');
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const accountCombo: ComboboxOption[] = [
    { value: NONE, label: t('cat.defaultAccountNone') },
    ...acctOptions(accounts, 'expense'),
  ];
  const acctName = (id: string | null): string => {
    if (!id) return t('cat.none');
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.code} — ${a.name}` : t('cat.none');
  };

  const add = async (): Promise<void> => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await createExpenseCategory({ name: name.trim(), defaultAccountId: accountId && accountId !== NONE ? accountId : null });
      setName('');
      setAccountId(undefined);
      onChanged();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'conflict' ? t('errors.duplicateCategory') : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{t('cat.title')}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-3">
          {canWrite && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <FormField id="cat-name" label={t('cat.name')}>
                <Input id="cat-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('cat.namePlaceholder')} maxLength={150} />
              </FormField>
              <FormField id="cat-acct" label={t('cat.defaultAccount')}>
                <Combobox id="cat-acct" options={accountCombo} value={accountId} onChange={(v) => setAccountId(v ?? undefined)} placeholder={t('cat.searchAccount')} ariaLabel={t('cat.defaultAccount')} />
              </FormField>
              <Button onClick={() => void add()} loading={saving} disabled={!name.trim()}>{t('cat.add')}</Button>
            </div>
          )}
          <ul className="max-h-72 divide-y divide-border-subtle overflow-y-auto rounded-md border border-border">
            {categories.length === 0 ? (
              <li className="py-6 text-center text-sm text-fg-muted">{t('cat.empty')}</li>
            ) : (
              categories.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="font-medium text-fg">{c.name}</span>
                  <span className="text-xs text-fg-muted">{acctName(c.defaultAccountId)}</span>
                </li>
              ))
            )}
          </ul>
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>{t('cat.close')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Receipts (image / PDF attachments) for one expense — view, add, download, delete. */
function ReceiptsDialog({ expense, canWrite, canDelete, onClose }: {
  expense: Expense | null;
  canWrite: boolean;
  canDelete: boolean;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation('expenses');
  const { t: tc } = useTranslation();
  const [items, setItems] = useState<Attachment[] | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const urlsRef = useRef<string[]>([]);
  const expenseId = expense?.id ?? null;

  const load = async (id: string): Promise<void> => {
    setItems(null);
    try {
      const list = await listAttachments('expense', id);
      setItems(list);
      const next: Record<string, string> = {};
      for (const a of list) {
        if (isImageMime(a.mimeType)) {
          try { const u = await fetchAttachmentObjectUrl(a.id); urlsRef.current.push(u); next[a.id] = u; } catch { /* skip */ }
        }
      }
      setThumbs(next);
    } catch { setItems([]); }
  };

  useEffect(() => {
    if (expenseId) void load(expenseId);
    return () => { urlsRef.current.forEach((u) => URL.revokeObjectURL(u)); urlsRef.current = []; };
     
  }, [expenseId]);

  const onPick = async (files: FileList): Promise<void> => {
    if (!expenseId || files.length === 0) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) await uploadAttachment('expense', expenseId, f);
      await load(expenseId);
    } catch { toast.error(tc('errors:server')); } finally { setBusy(false); }
  };
  const onDelete = async (a: Attachment): Promise<void> => {
    if (!expenseId) return;
    if (!(await confirmDialog({ message: t('receipts.confirmDelete'), tone: 'danger' }))) return;
    try { await deleteAttachment(a.id); await load(expenseId); } catch { toast.error(tc('errors:server')); }
  };
  const download = async (a: Attachment): Promise<void> => {
    try {
      const u = await fetchAttachmentObjectUrl(a.id);
      const link = document.createElement('a');
      link.href = u; link.download = a.filename; link.click();
      setTimeout(() => URL.revokeObjectURL(u), 10000);
    } catch { toast.error(tc('errors:server')); }
  };

  return (
    <Dialog open={!!expense} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{t('receipts.title', { number: expense?.expenseNumber ?? '' })}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-3">
          {canWrite && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => { if (e.target.files?.length) void onPick(e.target.files); if (e.target) e.target.value = ''; }}
              />
              <Button variant="secondary" onClick={() => fileRef.current?.click()} loading={busy}>
                <Upload className="me-1.5 h-4 w-4" aria-hidden />{t('receipts.add')}
              </Button>
            </>
          )}
          {!items ? (
            <Skeleton className="h-24 w-full" />
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('receipts.empty')}</p>
          ) : (
            <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto">
              {items.map((a) => (
                <li key={a.id} className="flex items-center gap-3 rounded-lg border border-border p-2">
                  {thumbs[a.id] ? (
                    <img src={thumbs[a.id]} alt="" className="h-12 w-12 shrink-0 rounded object-cover" />
                  ) : (
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded bg-bg-subtle text-fg-muted"><FileText className="h-5 w-5" aria-hidden /></span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-fg">{a.filename}</div>
                    <div className="text-xs text-fg-muted">{formatBytes(a.sizeBytes)}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => void download(a)} title={tc('actions.download', { defaultValue: 'Download' })} aria-label={tc('actions.download', { defaultValue: 'Download' })}>
                    <Download className="h-4 w-4" aria-hidden />
                  </Button>
                  {canDelete && (
                    <Button variant="ghost" size="sm" onClick={() => void onDelete(a)} title={tc('actions.delete')} aria-label={tc('actions.delete')}>
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end"><Button variant="secondary" onClick={onClose}>{t('cat.close')}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
